import { createRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import z from 'zod';
import {
  PATIENT_360_ROUTES,
  REPORT_LAYOUT_ROUTE_ID,
} from '@/features/patient-360/consts/routes';
import { patient360ViewRoute } from '@/features/patient-360/patient-360-view-route';
import {
  reportSearchParams,
  sharedSearchParams,
} from '@/features/patient-360/types/route-schema';
import { beforeLoadRouterGuard } from '@/integrations/tanstack-router/before-load-router-guard';
import { relativeTo } from '@/integrations/tanstack-router/relative-to';

const P360_VIEW_CHILD_STATIC_DATA = {
  home: PATIENT_360_ROUTES.patientView,
  parent: PATIENT_360_ROUTES.patientView,
} as const;

const reportLayoutRoute = createRoute({
  id: REPORT_LAYOUT_ROUTE_ID,
  getParentRoute: () => patient360ViewRoute,
  beforeLoad: beforeLoadRouterGuard([]),
  validateSearch: zodValidator(
    sharedSearchParams.extend({
      categoryName: z.string().optional(),
      ...reportSearchParams.shape,
    }),
  ),
}).lazy(() => import('./components/report-layout').then(({ Route }) => Route));

type Patient360Routes = typeof PATIENT_360_ROUTES;
const createReportContextRoute = ({
  parent,
  route,
}: {
  parent: Extract<
    Patient360Routes[keyof Patient360Routes],
    `${string}-reports`
  >;
  route: Extract<Patient360Routes[keyof Patient360Routes], `${string}/report`>;
}) =>
  createRoute({
    getParentRoute: () => reportLayoutRoute,
    staticData: {
      home: PATIENT_360_ROUTES.patientView,
      parent,
    },
    path: relativeTo(route, PATIENT_360_ROUTES.patientView),
  }).lazy(() =>
    import('./components/report-content').then(({ Route }) => Route),
  );

const labReportsRoute = createRoute({
  getParentRoute: () => reportLayoutRoute,
  staticData: P360_VIEW_CHILD_STATIC_DATA,
  path: relativeTo(
    PATIENT_360_ROUTES.labReports,
    PATIENT_360_ROUTES.patientView,
  ),
  validateSearch: zodValidator(sharedSearchParams),
}).lazy(() =>
  import('./components/lab-reports-page').then(({ Route }) => Route),
);

const labReportDateGroupRoute = createRoute({
  getParentRoute: () => reportLayoutRoute,
  staticData: {
    parent: PATIENT_360_ROUTES.labReports,
    home: PATIENT_360_ROUTES.patientView,
  },
  path: relativeTo(
    PATIENT_360_ROUTES.labReportDateGroup,
    PATIENT_360_ROUTES.patientView,
  ),
}).lazy(() =>
  import('./components/lab-report-date-group-page').then(({ Route }) => Route),
);

const labReportCategoryGroupRoute = createRoute({
  getParentRoute: () => reportLayoutRoute,
  staticData: {
    parent: PATIENT_360_ROUTES.labReports,
    home: PATIENT_360_ROUTES.patientView,
  },
  path: relativeTo(
    PATIENT_360_ROUTES.labReportCategoryGroup,
    PATIENT_360_ROUTES.patientView,
  ),
}).lazy(() =>
  import('./components/lab-report-category-group-page').then(
    ({ Route }) => Route,
  ),
);

const imagingReportsRoute = createRoute({
  getParentRoute: () => reportLayoutRoute,
  staticData: P360_VIEW_CHILD_STATIC_DATA,
  path: relativeTo(
    PATIENT_360_ROUTES.imagingReports,
    PATIENT_360_ROUTES.patientView,
  ),
  validateSearch: zodValidator(sharedSearchParams),
}).lazy(() =>
  import('./components/imaging-reports').then(({ Route }) => Route),
);
const imagingReportRoute = createReportContextRoute({
  parent: PATIENT_360_ROUTES.imagingReports,
  route: PATIENT_360_ROUTES.imagingReport,
});

const examReportsRoute = createRoute({
  getParentRoute: () => reportLayoutRoute,
  staticData: P360_VIEW_CHILD_STATIC_DATA,
  path: relativeTo(
    PATIENT_360_ROUTES.examReports,
    PATIENT_360_ROUTES.patientView,
  ),
  validateSearch: zodValidator(sharedSearchParams),
}).lazy(() => import('./components/exam-reports').then(({ Route }) => Route));
const examReportRoute = createReportContextRoute({
  parent: PATIENT_360_ROUTES.examReports,
  route: PATIENT_360_ROUTES.examReport,
});

const surgeryReportsRoute = createRoute({
  getParentRoute: () => reportLayoutRoute,
  staticData: P360_VIEW_CHILD_STATIC_DATA,
  path: relativeTo(
    PATIENT_360_ROUTES.surgeryReports,
    PATIENT_360_ROUTES.patientView,
  ),
  validateSearch: zodValidator(sharedSearchParams),
}).lazy(() =>
  import('./components/surgery-reports').then(({ Route }) => Route),
);
const surgeryReportRoute = createReportContextRoute({
  parent: PATIENT_360_ROUTES.surgeryReports,
  route: PATIENT_360_ROUTES.surgeryReport,
});

const pathologyReportsRoute = createRoute({
  getParentRoute: () => reportLayoutRoute,
  staticData: P360_VIEW_CHILD_STATIC_DATA,
  path: relativeTo(
    PATIENT_360_ROUTES.pathologyReports,
    PATIENT_360_ROUTES.patientView,
  ),
  validateSearch: zodValidator(sharedSearchParams),
}).lazy(() =>
  import('./components/pathology-reports').then(({ Route }) => Route),
);
const pathologyReportRoute = createReportContextRoute({
  parent: PATIENT_360_ROUTES.pathologyReports,
  route: PATIENT_360_ROUTES.pathologyReport,
});

export const reportRoutes = reportLayoutRoute.addChildren([
  labReportsRoute,
  labReportDateGroupRoute,
  labReportCategoryGroupRoute,
  imagingReportsRoute,
  imagingReportRoute,
  examReportsRoute,
  examReportRoute,
  surgeryReportsRoute,
  surgeryReportRoute,
  pathologyReportsRoute,
  pathologyReportRoute,
]);
